import { useEffect, useMemo, useState } from "react";
import { Modal, Select, Descriptions, Alert, Button, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  transferBmClients,
  fetchBranchMembers,
  BmTransferResult,
} from "../../../apis/branch.api";
import { downloadFile } from "../../misc/download-file";

interface Props {
  open: boolean;
  source: any | null;
  onClose: () => void;
}

// Permission ids that can own clients (mirrors backend BM_ROLE_IDS).
const BM_ROLE_IDS = [2, 3, 4, 11, 14];

const displayName = (u: any) =>
  `${u?.f_name ?? ""} ${u?.l_name ?? ""}`.trim() || u?.email || u?.contact;

type Phase = "select" | "preview" | "done";

const TransferClientsModal = ({ open, source, onClose }: Props) => {
  const [dest, setDest] = useState<string | undefined>(undefined);
  const [phase, setPhase] = useState<Phase>("select");
  const [loading, setLoading] = useState(false);
  const [bms, setBms] = useState<Array<any>>([]);
  const [bmsLoading, setBmsLoading] = useState(false);
  const [summary, setSummary] = useState<BmTransferResult | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBmsLoading(true);
    fetchBranchMembers()
      .then((res: any) => {
        if (cancelled) return;
        setBms(res ?? []);
      })
      .finally(() => {
        if (!cancelled) setBmsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const options = useMemo(
    () =>
      bms
        .filter(
          (m) =>
            BM_ROLE_IDS.includes(m?.user_permission) &&
            m?.user?.contact &&
            String(m.user.contact) !== String(source?.contact)
        )
        .map((m) => ({
          label: `${displayName(m.user)} — ${m.user.contact}`,
          value: String(m.user.contact),
        })),
    [bms, source]
  );

  const close = () => {
    setDest(undefined);
    setPhase("select");
    setSummary(null);
    onClose();
  };

  const runPreview = async () => {
    if (!source?.contact || !dest) return;
    setLoading(true);
    const res = await transferBmClients(String(source.contact), dest, true);
    setLoading(false);
    if (res) {
      setSummary(res);
      setPhase("preview");
    }
  };

  const runTransfer = async () => {
    if (!source?.contact || !dest) return;
    setLoading(true);
    const res = await transferBmClients(String(source.contact), dest, false);
    setLoading(false);
    if (res) {
      setSummary(res);
      setPhase("done");
    }
  };

  const downloadClients = () => {
    if (!summary?.clients?.length) return;
    const csvCell = (v: any) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["name", "contact", "source_bm", "dest_bm"];
    const rows = summary.clients.map((c) =>
      [c.name, c.contact, summary.source_bm_name, summary.dest_bm_name]
        .map(csvCell)
        .join(",")
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    downloadFile(
      blob,
      `bm_transfer_${source?.contact}_to_${dest}_${summary.clients_transferred}_clients.csv`
    );
  };

  const CountsTable = ({ data }: { data: BmTransferResult }) => (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label="Clients to transfer">
        {data.clients_transferred}
      </Descriptions.Item>
      <Descriptions.Item label="New assignments">
        {data.assignments_created}
      </Descriptions.Item>
      <Descriptions.Item label="Assignments reactivated">
        {data.assignments_reactivated}
      </Descriptions.Item>
      <Descriptions.Item label="Active tickets">
        {data.tickets_reassigned}
      </Descriptions.Item>
      <Descriptions.Item label="Estimates">
        {data.estimates_reassigned}
      </Descriptions.Item>
    </Descriptions>
  );

  const footer =
    phase === "select"
      ? [
          <Button key="cancel" onClick={close}>
            Cancel
          </Button>,
          <Button
            key="preview"
            type="primary"
            loading={loading}
            disabled={!dest}
            onClick={runPreview}
          >
            Preview
          </Button>,
        ]
      : phase === "preview"
      ? [
          <Button key="back" onClick={() => setPhase("select")}>
            Back
          </Button>,
          <Button
            key="download"
            icon={<DownloadOutlined />}
            disabled={!summary?.clients?.length}
            onClick={downloadClients}
          >
            Download Excel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading}
            onClick={runTransfer}
          >
            Confirm Transfer
          </Button>,
        ]
      : [
          <Button
            key="download"
            icon={<DownloadOutlined />}
            disabled={!summary?.clients?.length}
            onClick={downloadClients}
          >
            Download Excel
          </Button>,
          <Button key="done" type="primary" onClick={close}>
            Done
          </Button>,
        ];

  return (
    <Modal title="Transfer Clients" open={open} onCancel={close} footer={footer}>
      <div className="pb-2">
        <Space direction="vertical" size={2}>
          <span>
            <span className="th-font-weight-600">Source BM: </span>
            {source ? `${displayName(source)} — ${source.contact}` : "-"}
          </span>
          {(phase === "preview" || phase === "done") && summary && (
            <span>
              <span className="th-font-weight-600">Destination BM: </span>
              {summary.dest_bm_name} — {dest}
            </span>
          )}
        </Space>
      </div>

      {phase === "select" && (
        <>
          <div className="pb-3">
            <Alert
              type="info"
              showIcon
              message="Reassigns every active client of the source BM to the destination BM (assignments, active tickets, estimates). Writes directly to the DB and does not sync Kylas."
            />
          </div>
          <div className="pb-1 th-font-weight-600">Destination BM</div>
          <Select
            style={{ width: "100%" }}
            placeholder="Select destination BM"
            value={dest}
            onChange={setDest}
            options={options}
            loading={bmsLoading}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </>
      )}

      {phase === "preview" && summary && (
        <>
          <div className="pb-2">
            <Alert
              type="warning"
              showIcon
              message="Preview only — nothing has been changed yet. Confirm to apply. This is not reversible."
            />
          </div>
          <CountsTable data={summary} />
        </>
      )}

      {phase === "done" && summary && (
        <>
          <div className="pb-2 th-font-weight-600">Transfer complete</div>
          <CountsTable data={summary} />
        </>
      )}
    </Modal>
  );
};

export default TransferClientsModal;
